<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab tabid="tab-optimiser" :first="true">
        <i class="far fa-server"></i> Optimiser Queues
      </Tab>
      <Tab tabid="tab-optimiser-stats">
        <i class="far fa-chart-pie"></i> Optimiser Stats
      </Tab>
    </template>
    <template v-slot:tabcontent>

      <TabPage tabid="tab-optimiser" :first="true">
        <ContentTitle>Optimiser Scheduled Jobs</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>ID</th>
              <th>State</th>
              <th>Stock ID</th>
              <th>Account ID</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(job, index) of optimiserJobList" :key="index">
              <td class="font-weight-bold">{{job.id}}</td>
              <td>{{job.state}}</td>
              <td>{{job.data.stockId}}</td>
              <td>{{job.data.accountId}}</td>
            </tr>
          </template>
          <template v-slot:empty v-if="optimiserJobList.length == 0">No jobs</template>
        </StaticDataTable>

        <br /><br />
        <FormHorizontalRule />
        <br /><br />

        <ContentTitle>Optimiser BG Queue</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Status</th>
              <th>Count</th>
              <th>Stock</th>
              <th>Account ID</th>
              <th>Started</th>
              <th>Updated</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(queue, index) of optimiserQueueList" :key="index">
              <td>
                <span :class="['label', `label-optimiser-queue-status-${queue.status}`, 'text-uppercase']">{{queue.status}}</span>
              </td>
              <td>{{queue.count}}</td>
              <td>#{{queue.stockId}} - {{queue.stock.name}}</td>
              <td>{{queue.accountId}}</td>
              <td>{{secondsElapsed(queue.createdAt)}} seconds ago</td>
              <td>{{secondsElapsed(queue.updatedAt)}} seconds ago</td>
            </tr>
          </template>
          <template v-slot:empty v-if="optimiserQueueList.length == 0">No queued items</template>
        </StaticDataTable>
      </TabPage>

      <TabPage tabid="tab-optimiser-stats">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Optimiser Stats</ContentTitle>
          </div>
          <div class="col-lg-6">
            <ReportFilterPopover class="float-right" sizeClass="popover-xlarge">
              <template v-slot:popover>
                <h3>Date Range</h3>
                <ReportDateRangeSelect @updateStartDate="optimiserStatStartDate = $event" @updateEndDate="optimiserStatEndDate = $event" />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{optimiserStatStartDate | formatDate}} - {{optimiserStatEndDate | formatDate}}
                </div>
              </template>
            </ReportFilterPopover>
          </div>
        </div>
          
        <ContentTitle>Best Jobs</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Stock</th>
              <th>Panels</th>
              <th>Glass Levels</th>
              <th>Input Sheets</th>
              <th>Output Sheets</th>
              <th>Processing Time</th>
              <th>Request Time</th>
              <th>Total Time</th>
              <th>Timestamp</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="job of optimiserStats.bestJobList" :key="job.id">
              <td class="font-weight-bold"><LinkStock :id="job.stock.id" :name="job.stock.name" /></td>
              <td>{{job.panelCount}}</td>
              <td>{{job.glassLevelCount}}</td>
              <td>{{job.inputSheetCount}}</td>
              <td>{{job.outputSheetCount}}</td>

              <td>{{job.processingTime}} ms</td>
              <td>{{job.requestTime}} ms</td>
              <td>{{job.totalTime}} ms</td>
              <td>{{formatDateCustom(job.createdAt, 'DD/MM/YY HH:mm:ss')}}</td>
            </tr>
          </template>
        </StaticDataTable>

        <br />
        <FormHorizontalRule />
        <br />
          
        <ContentTitle>Worst Jobs</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Stock</th>
              <th>Panels</th>
              <th>Glass Levels</th>
              <th>Input Sheets</th>
              <th>Output Sheets</th>
              <th>Processing Time</th>
              <th>Request Time</th>
              <th>Total Time</th>
              <th>Timestamp</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="job of optimiserStats.worstJobList" :key="job.id">
              <td class="font-weight-bold"><LinkStock :id="job.stock.id" :name="job.stock.name" /></td>
              <td>{{job.panelCount}}</td>
              <td>{{job.glassLevelCount}}</td>
              <td>{{job.inputSheetCount}}</td>
              <td>{{job.outputSheetCount}}</td>

              <td>{{job.processingTime}} ms</td>
              <td>{{job.requestTime}} ms</td>
              <td>{{job.totalTime}} ms</td>
              <td>{{formatDateCustom(job.createdAt, 'DD/MM/YY HH:mm:ss')}}</td>
            </tr>
          </template>
        </StaticDataTable>
        
        <br />
        <FormHorizontalRule />
        <br />

        <div class="row no-gutters">
          <div class="col-8 pr-2">
            <ContentTitle>Recent Jobs</ContentTitle>
            <StaticDataTable class="static-table-list" variant="table-sm">
              <template v-slot:header>
                <tr>
                  <th>Stock</th>
                  <th>Panels</th>
                  <th>Glass Levels</th>
                  <th>Input Sheets</th>
                  <th>Output Sheets</th>
                  <th>Job Time</th>
                  <th>Timestamp</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="job of optimiserStats.recentJobList" :key="job.id">
                  <td class="font-weight-bold"><LinkStock :id="job.stock.id" :name="job.stock.name" /></td>
                  <td>{{job.panelCount}}</td>
                  <td>{{job.glassLevelCount}}</td>
                  <td>{{job.inputSheetCount}}</td>
                  <td>{{job.outputSheetCount}}</td>
                  <td>{{job.totalTime}} ms</td>
                  <td>{{formatDateCustom(job.createdAt, 'DD/MM/YY HH:mm:ss')}}</td>
                </tr>
              </template>
            </StaticDataTable>            
          </div>
          <div class="col-4 pl-2">
            <ContentTitle>Stock Stats</ContentTitle>
            <StaticDataTable class="static-table-list" variant="table-sm">
              <template v-slot:header>
                <tr>
                  <th>Stock</th>
                  <th>Total Jobs</th>
                  <th>Avg. Processing Time</th>
                  <th>Avg. Request Time</th>
                  <th>Avg. Job Time</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="stock of optimiserStats.averageStockList" :key="stock.stockId">
                  <td class="font-weight-bold"><LinkStock :id="stock.stockId" :name="stock.stockName" /></td>
                  <td>{{stock.totalJobs}}</td>
                  <td>{{Math.round(stock.avgProcessingTime)}} ms</td>
                  <td>{{Math.round(stock.avgRequestTime)}} ms</td>
                  <td>{{Math.round(stock.avgTotalTime)}} ms</td>              
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>


    </template>
  </TabbedBox>
</template>

<style scoped>
.debug-output {
  white-space: pre;
  line-height: 18px;
  font-size: 12px;
  font-family: monospace, monospace;
  overflow-x: auto
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import form from '@/mixins/form'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

export default {
  name: "DeveloperOptimiser",
  mixins: [form],
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  data() {
    return {
      optimiserJobList: [],
      optimiserQueueList: [],
      optimiserStats: {
        bestJobList: [],
        worstJobList: [],
        recentJobList: [],
        averageStockList: []
      },

      optimiserStatStartDate: moment().subtract(14, 'days').toISOString(),
      optimiserStatEndDate: moment().toISOString(),
    }
  },
  watch: {
    optimiserStatStartDate: function() { this.reloadOptimiserInfo() },
    optimiserStatEndDate: function() { this.reloadOptimiserInfo() },
  },
  mounted() {
    this.$options.optimiserReloadInterval = setInterval(this.reloadOptimiserInfo, 3000)

    this.reloadOptimiserInfo()
  },
  beforeDestroy () {
    clearInterval(this.$options.optimiserReloadInterval);
  },
  computed: {
  },
  methods: {
    reloadOptimiserInfo() {
      let params = {
        startDate: this.optimiserStatStartDate,
        endDate: this.optimiserStatEndDate
      }

      this.$apiService.developerService.developerGetOptimiserInfo(params)
      .then(response => {
        this.optimiserJobList = response.data.optimiserJobList
        
        this.optimiserStats.bestJobList = response.data.optimiserStats.bestJobList
        this.optimiserStats.worstJobList = response.data.optimiserStats.worstJobList
        this.optimiserStats.recentJobList = response.data.optimiserStats.recentJobList
        this.optimiserStats.averageStockList = response.data.optimiserStats.averageStockList

        // Collate the queue
        this.optimiserQueueList = []
        response.data.optimiserQueueList.forEach(queue => {
          var existing = this.optimiserQueueList.find(item => {
            return item.stockId == queue.stockId && item.status == queue.status
          })
          if(existing) {
            existing.updatedAt = queue.updatedAt
            existing.count++
          } else {
            var newItem = _.clone(queue)
            newItem.count = 1
            this.optimiserQueueList.push(newItem)
          }
        })
      })
    },
    objectToJSON(obj) {
      return JSON.stringify(obj || {}, null, 2)
    },
    secondsElapsed(time) {
      return moment().diff(time, 'seconds')
    }
  }
}

</script>